<template>
  <div class="tm-report">
    <div class="report-toolbar">
      <div class="report-title">
        Отчёт по клиенту (в стиле ТМ) — Эфес
        <span v-if="report" class="report-sub">
          {{ fmtNum(report.summary.trips_total) }} рейсов, период по общим фильтрам
        </span>
      </div>
      <select v-model="activeReport" class="report-select" title="Какой отчёт показать">
        <option v-for="r in REPORTS" :key="r.id" :value="r.id">{{ r.name }}</option>
      </select>
      <button
        class="btn-clear export-btn"
        type="button"
        :disabled="isExporting || !report"
        title="Полный отчёт CSV: сводка, фонд по месяцам, менеджеры, сегменты, статусы, срывы"
        @click="exportCsv"
      >
        {{ isExporting ? 'Выгрузка…' : '⤓ CSV' }}
      </button>
    </div>

    <my-loader v-if="isLoading" />
    <div v-else-if="errorText" class="empty-table error">{{ errorText }}</div>
    <div v-else-if="!report || !report.summary || !report.summary.trips_total" class="empty-table">
      Нет данных по выбранным фильтрам
    </div>

    <template v-else>
      <div v-if="activeReport === 'summary'" class="cards">
        <div class="card">
          <div class="card-label">Рейсы / Закрытые / Срывы</div>
          <div class="card-value">
            {{ fmtNum(report.summary.trips_total) }} /
            {{ fmtNum(report.summary.closed_total) }} /
            {{ fmtNum(report.summary.breakdown_total) }}
          </div>
        </div>
        <div class="card">
          <div class="card-label">Ожидаемая комиссия</div>
          <div class="card-value">{{ fmtMoney(report.summary.expected_commission) }}</div>
        </div>
        <div class="card">
          <div class="card-label">Чистая комиссия (минус Фонд Кипиай)</div>
          <div class="card-value">{{ fmtMoney(report.summary.net_commission) }}</div>
        </div>
        <div class="card">
          <div class="card-label">Финансовый результат (минус штрафы за срывы)</div>
          <div class="card-value" :class="{ warn: report.summary.financial_result < 0 }">
            {{ fmtMoney(report.summary.financial_result) }}
          </div>
        </div>
        <div class="card">
          <div class="card-label">Положили в Фонд Кипиай</div>
          <div class="card-value">{{ fmtMoney(report.summary.kpi_fund_put) }}</div>
        </div>
        <div class="card">
          <div class="card-label">Взяли из фонда Кипиай</div>
          <div class="card-value">{{ fmtMoney(report.summary.kpi_fund_take) }}</div>
        </div>
        <div class="card">
          <div class="card-label">Разница фонда за период</div>
          <div class="card-value" :class="{ warn: report.summary.kpi_fund_diff < 0 }">
            {{ fmtMoney(report.summary.kpi_fund_diff) }}
          </div>
        </div>
        <div class="card">
          <div class="card-label">Автолидер</div>
          <div class="card-value">{{ fmtMoney(report.summary.autoleader_total) }}</div>
        </div>
        <div class="card">
          <div class="card-label">Штрафы за срывы</div>
          <div class="card-value warn">{{ fmtMoney(report.summary.breakdown_penalty_total) }}</div>
        </div>
        <div class="card">
          <div class="card-label">Σ ставка заказчика / перевозчика</div>
          <div class="card-value small">
            {{ fmtMoney(report.summary.zack_stavka_total) }} /
            {{ fmtMoney(report.summary.per_stavka_total) }}
          </div>
        </div>
      </div>

      <template v-if="activeReport === 'fund'">
      <div class="section-title">Фонд Кипиай по месяцам (нарастающим итогом с начала года)</div>
      <div class="table-scroll">
        <table class="stat-table">
          <thead>
            <tr>
              <th>Месяц</th>
              <th class="num">Положили</th>
              <th class="num">Взяли</th>
              <th class="num">Разница</th>
              <th class="num">Положили YTD</th>
              <th class="num">Взяли YTD</th>
              <th class="num">Разница YTD</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in report.fund" :key="row.month">
              <td>{{ row.month }}</td>
              <td class="num">{{ fmtMoney(row.kpi_fund_put) }}</td>
              <td class="num">{{ fmtMoney(row.kpi_fund_take) }}</td>
              <td class="num" :class="{ 'warn-cell': row.kpi_fund_diff < 0 }">
                {{ fmtMoney(row.kpi_fund_diff) }}
              </td>
              <td class="num">{{ fmtMoney(row.kpi_fund_put_ytd) }}</td>
              <td class="num">{{ fmtMoney(row.kpi_fund_take_ytd) }}</td>
              <td class="num" :class="{ 'warn-cell': row.kpi_fund_diff_ytd < 0 }">
                {{ fmtMoney(row.kpi_fund_diff_ytd) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </template>

      <template v-if="activeReport === 'by_manager'">
      <div class="section-title">По менеджерам (аналог MANREP)</div>
      <div class="table-scroll">
        <table class="stat-table">
          <thead>
            <tr>
              <th>Менеджер</th>
              <th class="num">Рейсы</th>
              <th class="num">Закрытые</th>
              <th class="num">Σ ставка заказчика</th>
              <th class="num">Σ ставка перевозчика</th>
              <th class="num">Ожидаемая комиссия</th>
              <th class="num">Чистая</th>
              <th class="num">Финрезультат</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in report.by_manager" :key="row.manager">
              <td>{{ row.manager }}</td>
              <td class="num">{{ fmtNum(row.trips_total) }}</td>
              <td class="num">{{ fmtNum(row.closed_total) }}</td>
              <td class="num">{{ fmtMoney(row.zack_stavka_total) }}</td>
              <td class="num">{{ fmtMoney(row.per_stavka_total) }}</td>
              <td class="num strong">{{ fmtMoney(row.expected_commission) }}</td>
              <td class="num">{{ fmtMoney(row.net_commission) }}</td>
              <td class="num" :class="{ 'warn-cell': row.financial_result < 0 }">
                {{ fmtMoney(row.financial_result) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      </template>

      <template v-if="activeReport === 'by_segment'">
      <div class="section-title">По сегментам</div>
      <div class="table-scroll">
        <table class="stat-table">
          <thead>
            <tr>
              <th>Сегмент</th>
              <th class="num">Рейсы</th>
              <th class="num">Закрытые</th>
              <th class="num">Положили в Фонд</th>
              <th class="num">Взяли из фонда</th>
              <th class="num">Автолидер</th>
              <th class="num">Ожидаемая комиссия</th>
              <th class="num">Чистая</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in report.by_segment" :key="row.segment">
              <td>{{ row.segment }}</td>
              <td class="num">{{ fmtNum(row.trips_total) }}</td>
              <td class="num">{{ fmtNum(row.closed_total) }}</td>
              <td class="num">{{ fmtMoney(row.kpi_fund_put) }}</td>
              <td class="num">{{ fmtMoney(row.kpi_fund_take) }}</td>
              <td class="num">{{ fmtMoney(row.autoleader_total) }}</td>
              <td class="num strong">{{ fmtMoney(row.expected_commission) }}</td>
              <td class="num">{{ fmtMoney(row.net_commission) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </template>

      <template v-if="activeReport === 'by_status'">
      <div class="section-title">По статусам</div>
      <div class="table-scroll">
        <table class="stat-table">
          <thead>
            <tr>
              <th>Статус</th>
              <th class="num">Рейсы</th>
              <th class="num">Ожидаемая комиссия</th>
              <th class="num">Штрафы за срывы</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in report.by_status" :key="row.status">
              <td>{{ row.status }}</td>
              <td class="num">{{ fmtNum(row.trips_total) }}</td>
              <td class="num">{{ fmtMoney(row.expected_commission) }}</td>
              <td class="num warn-cell">{{ fmtMoney(row.breakdown_penalty) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </template>

      <template v-if="activeReport === 'breakdowns'">
      <div class="section-title">Срывы — штрафы (ключевой клиент — 100% ставки без НДС, прочие — 25%)</div>
      <div class="table-scroll">
        <table class="stat-table">
          <thead>
            <tr>
              <th>№ ТМ</th>
              <th>Карточка</th>
              <th>Дата</th>
              <th>Маршрут</th>
              <th>Тип клиента</th>
              <th class="num">База штрафа (без НДС)</th>
              <th class="num">Ставка</th>
              <th class="num">Штраф</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in report.breakdowns" :key="row.vhnum || idx">
              <td>{{ row.vhnum || '—' }}</td>
              <td class="mono">{{ shortId(row.distr_id) }}</td>
              <td>{{ fmtDay(row.stat_date) }}</td>
              <td>{{ row.city_out }} → {{ row.city_in }}</td>
              <td>{{ clientTypeLabel(row.client_type) }}</td>
              <td class="num">{{ fmtMoney(row.penalty_base) }}</td>
              <td class="num">{{ fmtRate(row.penalty_rate) }}</td>
              <td class="num strong warn-cell">{{ fmtMoney(row.penalty) }}</td>
            </tr>
            <tr v-if="!(report.breakdowns || []).length">
              <td colspan="8" class="empty-cell">Срывов в периоде нет</td>
            </tr>
          </tbody>
        </table>
      </div>
      </template>
    </template>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { downloadTmReportCsv, fetchTmReport } from '@/backend/statisticsApi';
import { fmtMoney, fmtNum } from '../format';

export default {
  name: 'TmReportsTab',
  props: {
    filters: { type: Object, required: true },
    filtersKey: { type: String, required: true },
    isActive: { type: Boolean, default: false },
  },
  setup(props) {
    const reportQuery = useQuery({
      queryKey: computed(() => ['statistics', 'tm-report', props.filtersKey]),
      queryFn: () => fetchTmReport(props.filters),
      enabled: computed(() => props.isActive),
      staleTime: 60 * 1000,
      placeholderData: (previousData) => previousData,
    });
    const report = computed(() => reportQuery.data.value || null);
    const isLoading = computed(() => reportQuery.isLoading.value);
    const errorText = computed(() => {
      const error = reportQuery.error.value;
      if (!error) return '';
      return error?.response?.data?.detail || error?.message || 'Не удалось загрузить отчёт';
    });

    const isExporting = ref(false);
    const exportCsv = async () => {
      isExporting.value = true;
      try {
        await downloadTmReportCsv(props.filters);
      } catch (e) {
        console.error('TM report CSV export failed', e);
      } finally {
        isExporting.value = false;
      }
    };

    const REPORTS = [
      { id: 'summary', name: 'Сводка' },
      { id: 'fund', name: 'Фонд Кипиай (по месяцам + YTD)' },
      { id: 'by_manager', name: 'Менеджеры (MANREP)' },
      { id: 'by_segment', name: 'Сегменты' },
      { id: 'by_status', name: 'Статусы' },
      { id: 'breakdowns', name: 'Срывы — штрафы' },
    ];
    const activeReport = ref('summary');

    const fmtDay = (value) => (value ? String(value).slice(0, 10) : '—');
    const fmtRate = (rate) => (rate == null ? '—' : `${Math.round(rate * 100)}%`);
    const clientTypeLabel = (t) => {
      if (t === 'key') return 'ключевой';
      if (!t) return '—';
      return t;
    };
    const shortId = (id) => (id ? String(id).slice(0, 8) : '—');

    return {
      report,
      isLoading,
      errorText,
      isExporting,
      exportCsv,
      REPORTS,
      activeReport,
      fmtDay,
      fmtRate,
      clientTypeLabel,
      shortId,
      fmtMoney,
      fmtNum,
    };
  },
};
</script>

<style scoped>
.tm-report {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.report-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.report-title {
  font-size: 16px;
  font-weight: 600;
}

.report-sub {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #667085;
}

.report-select {
  margin-left: auto;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
}

.btn-clear.export-btn {
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}

.btn-clear.export-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.card {
  border: 1px solid #eaecf0;
  border-radius: 10px;
  padding: 10px 14px;
  background: #fff;
}

.card-label {
  font-size: 12px;
  color: #667085;
  margin-bottom: 4px;
}

.card-value {
  font-size: 17px;
  font-weight: 600;
}

.card-value.small {
  font-size: 14px;
}

.card-value.warn {
  color: #d92d20;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
}

.table-scroll {
  overflow-x: auto;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stat-table th {
  text-align: left;
  padding: 8px 10px;
  background: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  font-weight: 600;
  white-space: nowrap;
}

.stat-table td {
  padding: 7px 10px;
  border-bottom: 1px solid #f2f4f7;
  white-space: nowrap;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.stat-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.stat-table .strong {
  font-weight: 600;
}

.stat-table .warn-cell {
  color: #d92d20;
}

.stat-table .mono {
  font-family: monospace;
  font-size: 12px;
}

.stat-table .empty-cell {
  text-align: center;
  color: #667085;
}

.empty-table {
  padding: 24px;
  text-align: center;
  color: #667085;
}

.empty-table.error {
  color: #d92d20;
}
</style>
